// =====================================================================
// TARPI — Product Comparator (comparador.html)
// Persisted list via window.TARPI_compare
// =====================================================================

const { useState: cmpState, useEffect: cmpEffect, useMemo: cmpMemo, useSyncExternalStore: cmpSync } = React;

function cmpUseList() {
  return cmpSync(
    (cb) => window.TARPI_compare.subscribe(cb),
    () => window.TARPI_compare.get(),
    () => window.TARPI_compare.get()
  );
}
function cmpUseCart() {
  return cmpSync(
    (cb) => window.TARPI_store.subscribe(cb),
    () => window.TARPI_store.get(),
    () => window.TARPI_store.get()
  );
}

function ProductPicker({ onPick, currentIds, lineFilter, onClose }) {
  const [line, setLine] = cmpState(lineFilter || "all");
  const [query, setQuery] = cmpState("");
  const filtered = window.TARPI_PRODUCTS.filter(p => {
    if (line !== "all" && p.line !== line) return false;
    if (currentIds.includes(p.id)) return false;
    if (query && !`${p.name} ${p.categoryLabel}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="cmp-picker-overlay" onClick={onClose}>
      <div className="cmp-picker" onClick={e => e.stopPropagation()}>
        <div className="cmp-picker-head">
          <h3>Agregar producto al comparador</h3>
          <button className="product-detail-close" onClick={onClose} aria-label="Cerrar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="cmp-picker-toolbar">
          <div className="catalog-tabs">
            <button className={`catalog-tab ${line === "all" ? "active" : ""}`} onClick={() => setLine("all")}>Todas</button>
            <button className={`catalog-tab ${line === "rumax" ? "active" : ""}`} onClick={() => setLine("rumax")}>RUMAX</button>
            <button className={`catalog-tab ${line === "ovomax" ? "active" : ""}`} onClick={() => setLine("ovomax")}>OVOMAX</button>
          </div>
          <input
            className="cmp-picker-search"
            type="search"
            placeholder="Buscar producto…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="cmp-picker-list">
          {filtered.length === 0 && <p style={{padding: 40, textAlign: "center", color: "var(--ink-500)"}}>Sin resultados</p>}
          {filtered.map(p => (
            <button key={p.id} className={`cmp-picker-item line-${p.line}`} onClick={() => { onPick(p.id); onClose(); }}>
              <div className="cmp-picker-bag">
                {(p.imageThumb || p.image)
                  ? <img src={p.imageThumb || p.image} alt={p.name} className="cmp-picker-photo" loading="lazy" />
                  : <window.FeedBag line={p.line} name={p.name} category={p.categoryLabel} small />
                }
              </div>
              <div>
                <span className="cmp-picker-meta">{p.line.toUpperCase()} · {p.categoryLabel}</span>
                <span className="cmp-picker-name">{p.name}</span>
                <span className="cmp-picker-sub">{p.species} · {p.stage}</span>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" style={{color: "var(--ink-400)"}}>
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Comparador() {
  const list = cmpUseList();
  const cart = cmpUseCart();
  const cartIds = new Set(cart.map(c => c.id));
  const products = list.map(id => window.TARPI_PRODUCTS.find(p => p.id === id)).filter(Boolean);

  const params = new URLSearchParams(location.search);
  const lineFilter = params.get("line") || null;
  const [pickerOpen, setPickerOpen] = cmpState(false);

  // On first load, if list is empty and ?line= specified, suggest starter products
  cmpEffect(() => {
    if (products.length === 0 && lineFilter) {
      // pre-populate 2 representative products from the line for demo
      const starters = window.TARPI_PRODUCTS.filter(p => p.line === lineFilter).slice(0, 2);
      starters.forEach(p => window.TARPI_compare.add(p.id));
    }
  }, []);

  // Get union of all spec labels across the selected products, preserving order
  const allSpecLabels = cmpMemo(() => {
    const seen = new Set();
    const ordered = [];
    products.forEach(p => p.specs.forEach(s => {
      if (!seen.has(s.label)) {
        seen.add(s.label);
        ordered.push({ label: s.label, unit: s.unit });
      }
    }));
    return ordered;
  }, [products]);

  // Get spec value for product or "—"
  const getSpec = (product, label) => {
    const s = product.specs.find(sp => sp.label === label);
    return s ? { value: s.value, unit: s.unit } : null;
  };

  // Determine max/min per row to highlight
  const rowExtremes = cmpMemo(() => {
    const out = {};
    allSpecLabels.forEach(({label}) => {
      const values = products.map(p => {
        const s = p.specs.find(sp => sp.label === label);
        return s ? s.value : null;
      });
      const nums = values.filter(v => typeof v === 'number');
      if (nums.length >= 2) {
        out[label] = { max: Math.max(...nums), min: Math.min(...nums) };
      }
    });
    return out;
  }, [products, allSpecLabels]);

  const addAllToCart = () => {
    products.forEach(p => { if (!cartIds.has(p.id)) window.TARPI_store.add(p); });
    window.dispatchEvent(new CustomEvent("tarpi:open-cart"));
  };

  return (
    <React.Fragment>
      {/* HERO */}
      <section className="cmp-hero">
        <div className="cmp-hero-inner">
          <span className="eyebrow">Comparador</span>
          <h1 className="display display-lg">Compará productos <em className="serif-italic gradient-text">lado a lado</em>.</h1>
          <p className="lede">
            Seleccioná hasta 4 productos TARPI y revisá sus niveles de garantía, especies, etapas y características en una tabla unificada. Lo que es máximo y mínimo por fila queda destacado automáticamente.
          </p>
          <div className="cmp-hero-meta">
            <span><strong>{products.length}</strong> de 4 productos seleccionados</span>
            {products.length > 0 && (
              <button className="cmp-clear" onClick={() => window.TARPI_compare.clear()}>Limpiar todo</button>
            )}
          </div>
        </div>
      </section>

      {/* TABLE / EMPTY */}
      {products.length === 0 ? (
        <section className="cmp-empty">
          <div className="cmp-empty-inner">
            <svg width="80" height="80" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1" style={{color: "var(--ink-300)"}}>
              <rect x="6" y="8" width="14" height="32" rx="2"/>
              <rect x="28" y="8" width="14" height="32" rx="2"/>
              <path d="M13 16h0M13 24h0M13 32h0M35 16h0M35 24h0M35 32h0" strokeWidth="3" strokeLinecap="round"/>
            </svg>
            <h3>Aún no agregaste productos</h3>
            <p>Empezá eligiendo productos desde el catálogo, o agregalos directamente desde aquí.</p>
            <button className="btn btn-primary" onClick={() => setPickerOpen(true)}>
              Agregar primer producto
              <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            </button>
            <div className="cmp-empty-shortcuts">
              <a href={(window.tarpiData?.homeUrl || '/') + 'lineas/rumax/'} className="btn btn-ghost">Ver línea RUMAX</a>
              <a href={(window.tarpiData?.homeUrl || '/') + 'lineas/ovomax/'} className="btn btn-ghost">Ver línea OVOMAX</a>
              <a href={(window.tarpiData?.homeUrl || '/') + 'lineas/suimax/'} className="btn btn-ghost">Ver línea SUIMAX</a>
            </div>
          </div>
        </section>
      ) : (
        <section className="cmp-table-wrap">
          {/* Print-only header — documento corporativo (A4 landscape) */}
          <div className="print-only print-header">
            <div className="print-header-brand">
              <img src={`${window.TARPI_ASSETS || 'assets'}/tarpi-logo.png`} alt="TARPI" />
              <div className="meta">
                <h1>Comparativa de productos</h1>
                <p>TARPI · Nutrición Animal</p>
              </div>
            </div>
            <div className="print-header-info">
              <strong>{new Date().toLocaleDateString('es-PY', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
              {products.length} producto{products.length !== 1 ? 's' : ''} comparado{products.length !== 1 ? 's' : ''}<br/>
              tarpy.com.py · comercial@tarpy.com.py
            </div>
          </div>

          <div className="cmp-table-scroll">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th className="cmp-th-label"></th>
                  {products.map(p => (
                    <th key={p.id} className={`cmp-th line-${p.line}`}>
                      <button
                        className="cmp-remove"
                        onClick={() => window.TARPI_compare.remove(p.id)}
                        aria-label={`Quitar ${p.name}`}
                        title="Quitar del comparador"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                      </button>
                      <div className="cmp-th-bag">
                        {(p.image || p.imageThumb)
                          ? <img src={p.image || p.imageThumb} alt={p.name} className="cmp-th-photo" loading="lazy" />
                          : <window.FeedBag line={p.line} name={p.name} category={p.categoryLabel} />
                        }
                      </div>
                      <span className="cmp-th-tag">{p.line.toUpperCase()} · {p.categoryLabel}</span>
                      <h4>{p.name}</h4>
                      <span className="cmp-th-species">{p.species} · {p.stage}</span>
                      <button
                        className={`btn btn-primary cmp-quote-btn ${cartIds.has(p.id) ? 'in-cart' : ''}`}
                        onClick={() => window.TARPI_store.add(p)}
                      >
                        {cartIds.has(p.id) ? "✓ En cotización" : "Agregar a cotización"}
                      </button>
                    </th>
                  ))}
                  {products.length < 4 && (
                    <th className="cmp-th cmp-th-add">
                      <button className="cmp-add-btn" onClick={() => setPickerOpen(true)}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 8v8M8 12h8"/>
                        </svg>
                        <span>Agregar producto</span>
                        <span className="cmp-add-sub">{4 - products.length} disponibles</span>
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="cmp-section-row">
                  <td colSpan={products.length + (products.length < 4 ? 2 : 1)}>Descripción</td>
                </tr>
                <tr>
                  <td className="cmp-row-label">Indicaciones</td>
                  {products.map(p => (
                    <td key={p.id} className="cmp-cell cmp-cell-text">{p.description}</td>
                  ))}
                  {products.length < 4 && <td></td>}
                </tr>
                <tr>
                  <td className="cmp-row-label">Validez</td>
                  {products.map(p => <td key={p.id} className="cmp-cell">{p.validity}</td>)}
                  {products.length < 4 && <td></td>}
                </tr>
                <tr>
                  <td className="cmp-row-label">Restricciones</td>
                  {products.map(p => <td key={p.id} className="cmp-cell cmp-cell-text">{p.restrictions}</td>)}
                  {products.length < 4 && <td></td>}
                </tr>

                <tr className="cmp-section-row">
                  <td colSpan={products.length + (products.length < 4 ? 2 : 1)}>Niveles de garantía</td>
                </tr>
                {allSpecLabels.map(({label, unit}) => {
                  const ext = rowExtremes[label];
                  return (
                    <tr key={label}>
                      <td className="cmp-row-label">{label} <span className="cmp-row-unit">({unit})</span></td>
                      {products.map(p => {
                        const s = getSpec(p, label);
                        if (!s) return <td key={p.id} className="cmp-cell cmp-cell-empty">—</td>;
                        const isMax = ext && s.value === ext.max && ext.max !== ext.min;
                        const isMin = ext && s.value === ext.min && ext.max !== ext.min;
                        return (
                          <td key={p.id} className={`cmp-cell cmp-cell-spec ${isMax ? 'cmp-max' : ''} ${isMin ? 'cmp-min' : ''}`}>
                            <span className="cmp-spec-num">{s.value}</span>
                            <span className="cmp-spec-unit">{s.unit}</span>
                            {isMax && <span className="cmp-spec-badge">▲ Mayor</span>}
                            {isMin && <span className="cmp-spec-badge">▼ Menor</span>}
                          </td>
                        );
                      })}
                      {products.length < 4 && <td></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Print-only footer — leyenda + nota corporativa */}
          <div className="print-only print-footer">
            <div className="legend">
              <span><span className="swatch max"></span> Mayor valor por fila</span>
              <span><span className="swatch min"></span> Menor valor por fila</span>
            </div>
            <div className="print-footer-note">
              Los valores corresponden a niveles de garantía declarados. Para tu plan nutricional personalizado, consultá con tu asesor técnico TARPI.
            </div>
          </div>

          <div className="cmp-action-bar">
            <span style={{color: "var(--ink-500)", fontSize: 14}}>
              <strong style={{color: "var(--ink-900)"}}>{products.length}</strong> producto{products.length !== 1 ? 's' : ''} en comparación
            </span>
            <div style={{display: "flex", gap: 12}}>
              <button className="btn btn-ghost" onClick={() => window.print()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                Exportar PDF
              </button>
              <button className="btn btn-primary" onClick={addAllToCart}>
                Cotizar los {products.length} productos
                <svg className="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>

        </section>
      )}

      {pickerOpen && (
        <ProductPicker
          onPick={(id) => window.TARPI_compare.add(id)}
          currentIds={list}
          lineFilter={lineFilter}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </React.Fragment>
  );
}

const cmpMount = document.getElementById("comparador-page");
if (cmpMount) ReactDOM.createRoot(cmpMount).render(<Comparador />);
